import { createAction } from "@reduxjs/toolkit";
import { getRedirectToForExplicitLogin } from "common/utilities/browser/url/url";
import * as API from "core/api/api";
import { StatusCode } from "core/api/apiHelpers";
import * as I from "core/api/coreApiClient";
import { updateCommand } from "core/redux/actions/command/commandActions";
import type { CommandUpdateActionOutput } from "core/redux/actions/command/commandActions.types";
import { formatConfiguration } from "core/redux/actions/configuration/configurationActions";
import { redirectToPageAction } from "core/redux/actions/ui/uiActions";
import { getStore } from "core/redux/store/configureStore";
import type { AppThunk, AppThunkDispatch } from "core/redux/store/configureStore.types";

const formatAppState = (appState: I.AppState | undefined, imgRootUrl: string): I.AppState => ({
  ...appState,
  appConfiguration: appState?.appConfiguration ? formatConfiguration(appState.appConfiguration, imgRootUrl) : undefined,
});

// Login
export const loggingInUser = createAction("user/loggingInUser");

export const loggedInUser = createAction(
  "user/loggedInUser",
  (appState: I.AppState | undefined, imgRootUrl: string) => ({
    payload: { appState, imgRootUrl },
  })
);

export const loginFailed = createAction("user/loginFailed", (errorMsg: string) => ({
  payload: { errorMsg },
}));

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    PasswordCredential: any;
  }
}

export const loginUser =
  (account: string, password: string): AppThunk =>
  async (dispatch) => {
    dispatch(loggingInUser());

    try {
      const payload = await API.login(account, password);

      if (payload) {
        /**
         * Login actions called asynchronously don't trigger browsers 'save password' feature.
         * PasswordCredential can be used to trigger is manually.
         * Note that it needs a password to work, it can't be an empty string.
         */
        if (window.PasswordCredential && password.length > 0) {
          const credential = new window.PasswordCredential({ id: account, password });
          navigator.credentials.store(credential);
        }

        const { imgRootUrl } = getStore().getState().configuration;
        dispatch(loggedInUser(formatAppState(payload, imgRootUrl), imgRootUrl));

        /* Explicit login, typically triggered from the old JSF client */
        if (window.location.pathname.match("/login")) {
          dispatch(redirectToPageAction(getRedirectToForExplicitLogin(), true));
        }
      }
    } catch (e) {
      dispatch(loginFailed("Login failed."));
    }
  };

export const dispatchLoginUser = (dispatch: AppThunkDispatch, account: string, password: string) => {
  dispatch(loginUser(account, password));
};

// Session
export const loadingSession = createAction("user/loadingSession");

export const sessionLoaded = createAction("user/sessionLoaded", (appState: I.AppState, imgRootUrl: string) => ({
  payload: {
    appState,
    imgRootUrl,
  },
}));

export const loadSessionFailed = createAction("user/loadSessionFailed");

export const loadSession =
  (imgRootUrl: string): AppThunk =>
  async (dispatch) => {
    console.log(imgRootUrl);
    dispatch(loadingSession());

    try {
      const payload = await API.getStatic();

      if (payload) {
        dispatch(sessionLoaded(formatAppState(payload, imgRootUrl), imgRootUrl));
      }
    } catch (e) {
      dispatch(loadSessionFailed());
    }
  };

export const checkSessionAlive =
  (sessionId?: string): AppThunk =>
  async () => {
    try {
      await API.getStatic(sessionId);
      return await Promise.resolve();
    } catch (e) {
      return await Promise.reject();
    }
  };

// Logout
export const loggingOutUser = createAction("user/loggingOutUser");

export const loggedOutUser = createAction("user/loggedOutUser", (manual?: boolean) => ({
  payload: { manual },
}));

export const logoutFailed = createAction("user/logoutFailed", (errorMsg: string) => ({
  payload: { errorMsg },
}));

export const logoutUser = (): AppThunk => async (dispatch) => {
  dispatch(loggingOutUser());

  try {
    await API.logout();
    dispatch(loggedOutUser(true));
    return await Promise.resolve();
  } catch (errorResponse) {
    // The user already logged out in a different tab
    if ((errorResponse as Response).status === StatusCode.UNAUTHORIZED) {
      dispatch(loggedOutUser(true));
      return await Promise.resolve();
    }

    dispatch(logoutFailed("There was a problem logging you out, please refresh the page and try again"));
    return await Promise.reject();
  }
};

export const updateUserSettingsRecentItemsPinnedCommand = (): I.Command => ({
  id: "updateUserSettingsRecentItemsPinned",
  title: "",
  active: true,
  url: "/userSettings/recent_items_pinned",
  httpVerb: I.HttpVerbConstants.PUT,
  commands: [],
});

export const updateUserSettingsRecentItemsPinned = (data?: boolean): AppThunk<CommandUpdateActionOutput> =>
  updateCommand(updateUserSettingsRecentItemsPinnedCommand(), { data });

export const userSessionExpired = createAction("user/userSessionExpired");
